// `/experiments` — the register, and `/experiments/$key` — one experiment.
//
// **This is where a per-experiment dashboard starts.** Pick the experiment, and
// the canvas arrives already scoped to it, bound to its control, and carrying
// the five starter tiles.
//
// **Neither page runs a warehouse query.** The register is assembled server-side
// from the studio sources' picker-column value cache (`bi/core/experiments.py`),
// so it is one Postgres read. The consequence a reader sees is `status:
// "pending"`: a cold environment enqueues the picker jobs and answers with what
// it knows so far, and this page says so rather than reporting an empty list as
// "no experiments".
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@bi/shared";
import type { StudioDashboardSummary } from "@bi/shared/types";

import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { experimentsQuery, studioDashboardsQuery } from "../lib/queries";

export function ExperimentsPage() {
  const register = useQuery(experimentsQuery);
  const dashboards = useQuery(studioDashboardsQuery);

  if (register.isPending) return <LoadingState label="Loading the experiment register…" />;
  if (register.isError) {
    return <ErrorState error={register.error} onRetry={() => void register.refetch()} />;
  }

  const rows = register.data.experiments;
  const countsByKey = countDashboards(dashboards.data?.dashboards ?? []);

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Experiments</h1>
        <Link to="/studio" className="bi-header__link">
          Studio
        </Link>
      </div>

      <PendingNote register={register.data} />

      {rows.length === 0 ? (
        <EmptyState
          title={
            register.data.sourcesSyncedAt
              ? "No experiment has rows in this environment's studio views yet"
              : "The studio views have not reached this environment's warehouse yet"
          }
          message={
            register.data.sourcesSyncedAt
              ? "The register is the union of every studio view's picker column. An empty one means those views are there and hold nothing, which is the normal state of a fresh environment."
              : "They arrive with the warehouse pin, on their own timer. Nothing here is broken."
          }
        />
      ) : (
        <div className="bi-catalog-grid">
          {rows.map((row) => (
            <Link
              key={row.key}
              to="/experiments/$experimentKey"
              params={{ experimentKey: row.key }}
              className="bi-catalog-card"
            >
              <span className="bi-catalog-card__title">{row.key}</span>
              <span className="bi-catalog-card__meta">
                {row.sources.length} source{row.sources.length === 1 ? "" : "s"} carry it
              </span>
              <span className="bi-catalog-card__meta">
                {countsByKey[row.key]
                  ? `${countsByKey[row.key]} dashboard${countsByKey[row.key] === 1 ? "" : "s"} filed here`
                  : "No dashboard yet"}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExperimentPage({ experimentKey }: { experimentKey: string }) {
  const navigate = useNavigate();
  const register = useQuery(experimentsQuery);
  const dashboards = useQuery(studioDashboardsQuery);

  if (register.isPending) return <LoadingState label="Loading the experiment register…" />;
  if (register.isError) {
    return <ErrorState error={register.error} onRetry={() => void register.refetch()} />;
  }

  const row = register.data.experiments.find((e) => e.key === experimentKey) ?? null;
  const filed = (dashboards.data?.dashboards ?? []).filter(
    (d) => d.experimentKey === experimentKey,
  );

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">{experimentKey}</h1>
        <Button
          variant="solid"
          tone="accent"
          size="sm"
          onClick={() =>
            void navigate({ to: "/studio/new", search: { experiment: experimentKey } })
          }
        >
          New dashboard for this experiment
        </Button>
      </div>

      <PendingNote register={register.data} />

      {row === null ? (
        <EmptyState
          title={
            register.data.status === "pending"
              ? "Not in the register yet"
              : "No studio view carries this experiment"
          }
          message={
            register.data.status === "pending"
              ? "The picker columns are still being computed. This page refreshes itself when they land."
              : `Nothing in this environment's studio views names ${experimentKey}. A dashboard built for it would draw blanks, which read as results. Check the key, or wait for the warehouse pin to carry the experiment.`
          }
        />
      ) : (
        <section className="bi-section">
          <h2 className="bi-page__title">Sources that carry it</h2>
          <ul className="bi-studio-rail__fields">
            {row.sources.map((ref) => (
              <li key={`${ref.sourceId}.${ref.column}`}>
                <Link
                  to="/studio/new"
                  search={{ experiment: experimentKey, source: ref.sourceId }}
                  className="bi-studio-field-row"
                >
                  <span className="bi-studio-field-row__head">
                    <span className="bi-studio-chip__name">{ref.sourceId}</span>
                    <span className="bi-studio-field-row__type">{ref.column}</span>
                  </span>
                  <span className="bi-studio-field-row__detail">
                    Build a chart over this source, scoped to {experimentKey}.
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bi-section">
        <h2 className="bi-page__title">Dashboards filed here</h2>
        {filed.length === 0 ? (
          <EmptyState
            title="Nothing yet"
            message="A dashboard created from this page is filed against this experiment and appears here."
          />
        ) : (
          <div className="bi-catalog-grid">
            {filed.map((d) => (
              <Link
                key={d.id}
                to="/studio/$id"
                params={{ id: d.id }}
                search={{ experiment: experimentKey }}
                className="bi-catalog-card"
              >
                <span className="bi-catalog-card__title">{d.title}</span>
                <span className="bi-catalog-card__meta">
                  {d.ownerEmail}
                  {d.visibility === "shared" ? ", shared" : ", private"}, version {d.version}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** The cold-cache sentence, shared by both pages.
 *
 *  Worth its own component because getting it wrong on either page has the same
 *  cost: a short register read as a complete one sends somebody looking for an
 *  experiment they were told does not exist. */
function PendingNote({
  register,
}: {
  register: { status: string; pendingColumns: readonly { sourceId: string }[] };
}) {
  if (register.status !== "pending") return null;
  const sources = [...new Set(register.pendingColumns.map((c) => c.sourceId))];
  return (
    <p className="bi-page__sub">
      Still reading the picker columns of {sources.join(", ")}, so this list may be short. It
      fills in on its own; nothing needs pressing.
    </p>
  );
}

function countDashboards(rows: readonly StudioDashboardSummary[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    if (row.experimentKey) counts[row.experimentKey] = (counts[row.experimentKey] ?? 0) + 1;
  }
  return counts;
}
